import React from 'react';
import { Leaf, Recycle, Heart, ShieldCheck } from 'lucide-react';
import useMetaTags from "../hooks/useMetaTags"; // Adjust path based on your project structure

const MissionAndAbout = () => {
  useMetaTags({
    title: "About Us | Organic Pads - Natural & Eco-Friendly Period Care",
    description: "Learn about our mission to provide safe, chemical-free, and eco-friendly sanitary pads. We believe in sustainable menstrual care for a healthier future.",
    keywords: "about organic pads, sustainable menstrual care, eco-friendly period products, natural sanitary pads, organic period care",
    author: "Organic Pads",
    robots: "index, follow",
    canonical: "/about",
    ogTitle: "About Us | Organic Pads - Natural & Eco-Friendly Period Care",
    ogDescription: "We are committed to offering the best organic sanitary pads, ensuring a chemical-free and comfortable period experience. Join our sustainable mission!",
    ogImage: "https://organicpads.shop/logo.png",
    ogUrl: "https://organicpads.shop/about-us",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "About Us | Organic Pads - Natural & Eco-Friendly Period Care",
    twitterDescription: "Join our mission to make menstrual care safe, sustainable, and chemical-free. Learn more about Organic Pads today!",
    twitterImage: "https://organicpads.shop/logo.png",
    twitterSite: "@OrganicPads",
    twitterCreator: "@OrganicPads",
  });

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Organic Pads",
    "url": "https://organicpads.shop",
    "logo": "https://organicpads.shop/logo.png",
    "description": "Organic Pads is committed to providing eco-friendly, chemical-free, and sustainable menstrual care products for a healthier planet and better well-being.",
    "foundingDate": "2025",
    "founders": [
      {
        "@type": "Person",
        "name": "Nikunj Gohil"
      }
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+917203802493",
      "contactType": "customer service",
      "email": "organicpads.shop@gmail.com",
      "availableLanguage": ["English", "Hindi"]
    },
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61573042873807",
      "https://www.instagram.com/organicpads_/",
      "https://www.linkedin.com/company/organic-pads",
      "https://www.youtube.com/channel/UCG5-R0zTI-2DAM14ZC0ZD-A",
      "https://x.com/OrganicPads"
    ]
  }
 

  return (
    <>
    <div className="max-w-7xl mx-auto mt-10 px-4 py-16 space-y-32">
      {/* Mission Section */}
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 text-[#679729]">
            <Leaf size={20} />
            <span className="text-lg font-medium">Our Mission</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-800">Empowering Women Through Sustainable Choices</h2>
          <p className="text-gray-600 leading-relaxed">
            We believe every woman deserves access to safe, comfortable, and eco-friendly menstrual products. Our mission is to revolutionize feminine hygiene by providing 100% organic cotton pads that care for both your body and our planet.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 pt-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#679729]/10 rounded-lg">
                <Recycle className="w-5 h-5 text-[#679729]" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Eco-Friendly</h3>
                <p className="text-sm text-gray-600">100% biodegradable & plastic-free packaging</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#679729]/10 rounded-lg">
                <Heart className="w-5 h-5 text-[#679729]" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Body-Safe</h3>
                <p className="text-sm text-gray-600">Chemical-free & gentle on sensitive skin</p>
              </div>
            </div>
          </div>
          <button className="mt-4 px-6 py-3 bg-[#679729] text-white rounded-lg hover:bg-[#679729]/90 transition-colors duration-300">
            Explore Our Products
          </button>
        </div>
        <div className="relative">
          <img 
            src= "https://images.unsplash.com/photo-1705147290951-59d9c69a1305?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGNvdHRvbnxlbnwwfHwwfHx8MA%3D%3D"
            alt="Organic cotton pads" 
            className="rounded-lg shadow-xl w-full"
          />
          <div className="absolute top-6 -left-6 bg-white p-4 rounded-lg shadow-lg">
            <div className="flex items-center gap-2 text-[#679729]">
              <ShieldCheck className="w-5 h-5" />
              <span className="font-medium">Certified Organic Cotton</span>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="order-2 lg:order-1 relative">
          <img 
            src="https://plus.unsplash.com/premium_photo-1664375262056-a8503d4d073c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8c2FuaXRhcnklMjBwYWR8ZW58MHx8MHx8fDA%3D" 
            alt="About our process" 
            className="rounded-lg shadow-xl w-full"
          />
          <div className="absolute top-1/2 -right-6 transform -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-xs">
            <h3 className="font-medium text-gray-800 mb-2">Our Impact</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[#679729]">
                <Recycle className="w-4 h-4" />
                <span className="text-sm">Reduced plastic waste by 50 tons</span>
              </div>
              <div className="flex items-center gap-2 text-[#679729]">
                <Heart className="w-4 h-4" />
                <span className="text-sm">Helped 100,000+ women switch to organic</span>
              </div>
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2 space-y-6">
          <div className="inline-flex items-center gap-2 text-[#679729]">
            <Heart size={20} />
            <span className="text-lg font-medium">About Us</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-800">Creating a Better Future for Women's Health</h2>
          <p className="text-gray-600 leading-relaxed">
            We're committed to providing premium organic cotton pads that prioritize both personal wellness and environmental sustainability. Our products are crafted with care, using only the finest organic materials and eco-conscious manufacturing processes.
          </p>
          <ul className="space-y-3">
            <li className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-[#679729]" />
              <span>100% organic cotton - no synthetics or chemicals</span>
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#679729]" />
              <span>Dermatologically tested for sensitive skin</span>
            </li>
            <li className="flex items-center gap-2">
              <Recycle className="w-5 h-5 text-[#679729]" />
              <span>Biodegradable materials and packaging</span>
            </li>
          </ul>
          <button className="mt-4 px-6 py-3 border-2 border-[#679729] text-[#679729] rounded-lg hover:bg-[#679729] hover:text-white transition-colors duration-300">
            Learn More About Us
          </button>
        </div>
      </div>
    </div>
    {/* Schema Markup */}
    <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>
    </>
  );
};

export default MissionAndAbout;